import { getCountries, getProductListingAttributes } from '@app/ducks';
import { applyCountryNameToAttribute, getCustomAttributesWithValue } from '@app/logic/products';
import { cn } from '@app/utils/cn';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Product } from '@typings';
import { prop, uniqBy } from 'ramda';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

const LARGE_CARD_NUMBER_OF_ATTRIBUTES = 4;
const MEDIUM_CARD_NUMBER_OF_ATTRIBUTES = 3;

interface Props {
  variant: Product.Standard;
  twoColumns?: boolean;
  productsGridSize: 'small' | 'medium' | 'large';
  shouldShowRrp?: boolean;
}

export const ProductCardAttributes = ({ variant, twoColumns = false, shouldShowRrp = false, productsGridSize }: Props) => {
  const { t } = useTranslation(['common']);
  const countries = useSelector(getCountries);
  const listingAttributes = useSelector(getProductListingAttributes);

  const hasRrp = isDefined(variant.rrp) && !isEmpty(variant.rrp);

  const [mediumCardAttributes, largeCardAttributes] = React.useMemo(() => {
    const filteredAttributes = uniqBy(prop('key'), [
      ...(shouldShowRrp && hasRrp ? [{ key: 'rrp', name: t('common:rrp'), value: variant.rrp }] : []),
      ...applyCountryNameToAttribute(getCustomAttributesWithValue(listingAttributes, variant), countries).filter(
        ({ value }) => !isEmpty(value),
      ),
    ]);

    return [filteredAttributes.slice(0, MEDIUM_CARD_NUMBER_OF_ATTRIBUTES), filteredAttributes.slice(0, LARGE_CARD_NUMBER_OF_ATTRIBUTES)];
  }, [shouldShowRrp, hasRrp, t, variant, listingAttributes, countries]);

  const attributes = productsGridSize === 'large' ? largeCardAttributes : mediumCardAttributes;

  return (
    <dl className={cn('columns-3xs gap-10', { 'columns-2': productsGridSize === 'large' && twoColumns })}>
      {attributes.map(({ name, value, key }) => (
        <div key={key} className="line-clamp-2 max-w-80 break-inside-avoid-column">
          <dt className="inline text-neutral-40 body-5">{name}: </dt>
          <dd className="inline body-4">{value}</dd>
        </div>
      ))}
    </dl>
  );
};
